/*
 * Copyright (c) 2024 metromorph softworks GmbH
 *
 * All rights reserved
 */

import * as React from "react";

import Component from "./component";

import AppContext from "../AppContext";

import { StaticLoadingScreen } from "../LoadingScreen";

import { useUserState } from "@ef/feature-user";
import { 
    useAppState, 
    useAppDispatch,
    setError,
    setMessage
} from "@ef/feature-app";
import { 
    logout, 
    useLoginState 
} from "@ef/feature-login";

function redirectAfterLogout() {
    setTimeout(() => {
        if (window.parent != window) {
            // if in iframe, inform parent -> keycloak
            window.parent.postMessage("logout-complete", "*");
        } else {
            // if in iframe, inform parent -> keycloak
            // Redirect to home (delete logout)
            window.location.search = "";
        }
    }, 500);
}

export default function BasicApp() {
    const [ init, setInit ] = React.useState<"loading"|"done"|"logout">("loading");
    const appstate = useAppState();
    const { initialized, error: loginError } = useLoginState();
    const user = useUserState();
    
    const dispatch = useAppDispatch();

    const { history } = React.useContext(AppContext);
  
    // Set Timeout -> if sth goes wrong, we do not wait forever 
    React.useEffect(() => {
        if (history?.location.search !== "?logout") return;
        const timer = setTimeout(redirectAfterLogout, 15 * 1000);
        return () => clearTimeout(timer);
    }, [history]);

    // Simple Logout on /app?logout
    // TODO@Rethink -> Is this really the right place? I doubt it....
    React.useEffect(() => {
    
        // Skip if we already now what we are doing....
        if (init !== "loading") return;

        // check logout
        if (history?.location.search === "?logout") {
            // Wait for Initialized
            if (!initialized) return;

            if (loginError) {
                dispatch(setMessage("Logout not possible: Login Error"));
                setInit("done");
                return;
            }
    
            setInit("logout");
            // React error -> setTimeout
            //  see: 
            //    * https://stackoverflow.com/a/76945127
            //    * https://github.com/facebook/react/issues/17355
            setTimeout(async () => {
                try {
                    // Trigger Logout if not anonymous
                    await dispatch(logout(user));
                    redirectAfterLogout();
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("Error during logout", e);
                }
            }, 1);
            return;
        }
        setInit("done");
    }, [history, dispatch, init, user, initialized, loginError]);

    if (init === "loading") {
        return <StaticLoadingScreen text="Loading…" />;
    }
    if (init === "logout") {
        return <StaticLoadingScreen text="Logging out…" />;
    }
    return (
        <Component 
            appstate={appstate} 
            resetError={() => dispatch(setError(undefined))}
        />
    );
}
